/**
 * SigningViewer - PDF.js Implementation
 *
 * Replaces Apryse WebViewer with PDF.js for native signing experience.
 * Maintains same interface as original SigningViewer.
 */

import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { SignatureModal } from './SignatureModal';
import { TextInputModal } from './TextInputModal';
import { PDFJSViewer } from '../../../components/pdf-viewer/PDFJSViewer';
import { SigningSessionData } from '../hooks/useSigningSession';
import styles from '../styles/SigningViewer.module.scss';

interface SigningViewerProps {
  session: SigningSessionData;
  onComplete: (
    signatureData: string,
    initialsData: string,
    fieldValues?: Record<
      string,
      {
        value: string;
        type: 'text' | 'date';
        coordinates: { x: number; y: number; width: number; height: number; page: number };
        signerId: string;
        signerEmail: string;
      }
    >
  ) => void;
  onCancel: () => void;
}

type SigningState = 'not_started' | 'signing' | 'ready_to_complete';

export const SigningViewerPdfJs: React.FC<SigningViewerProps> = ({ session, onComplete, onCancel }) => {
  const [signingState, setSigningState] = useState<SigningState>('not_started');
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [completedFields, setCompletedFields] = useState<Set<string>>(new Set());
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [modalFieldType, setModalFieldType] = useState<'signature' | 'initials'>('signature');
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [showTextInputModal, setShowTextInputModal] = useState(false);

  // The PDF is flattened, so it carries no form fields or annotations to render.
  // The native_signing_pdf has form field values rendered directly onto the canvas

  // Signature data storage
  const [savedSignatureData, setSavedSignatureData] = useState<{
    data: string;
    method: 'draw' | 'type' | 'upload';
    text?: string;
    font?: string;
  } | null>(null);
  const [savedInitialsData, setSavedInitialsData] = useState<{
    data: string;
    method: 'draw' | 'type' | 'upload';
    text?: string;
    font?: string;
  } | null>(null);
  const [signatureData, setSignatureData] = useState<string>('');

  // Field values for text/date fields
  const [fieldValues, setFieldValues] = useState<
    Record<
      string,
      {
        value: string;
        type: 'text' | 'date';
        coordinates: { x: number; y: number; width: number; height: number; page: number };
        signerId: string;
        signerEmail: string;
      }
    >
  >({});

  // Field signatures mapping
  const [fieldSignatures, setFieldSignatures] = useState<
    Map<string, { data: string; method: 'draw' | 'type' | 'upload'; text?: string; font?: string }>
  >(new Map());

  // Auto-fill date fields on mount
  useEffect(() => {
    const dateFields = session.fields.filter((f) => f.type === 'date');
    if (dateFields.length > 0) {
      const today = new Date();
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dateValue = `${months[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;

      const newFieldValues: typeof fieldValues = {};
      dateFields.forEach((field) => {
        const fieldKey = field.id;
        newFieldValues[fieldKey] = {
          value: dateValue,
          type: 'date',
          coordinates: {
            x: field.rect.x1,
            y: field.rect.y1,
            width: field.rect.x2 - field.rect.x1,
            height: field.rect.y2 - field.rect.y1,
            page: field.page,
          },
          signerId: session.signer.id.toString(),
          signerEmail: session.signer.email,
        };
        // Mark date fields as completed automatically
        setCompletedFields((prev) => new Set(prev).add(field.id));
      });
      setFieldValues(newFieldValues);
    }
  }, [session]);

  const requiredFields = session.fields.filter((f) => f.required);
  const remainingFields = requiredFields.length - completedFields.size;
  const allFieldsComplete = completedFields.size >= requiredFields.length;

  const handleStart = () => {
    setSigningState('signing');
    if (requiredFields.length > 0) {
      navigateToField(0);
    }
  };

  const handleNext = () => {
    let nextIndex = currentFieldIndex + 1;
    while (nextIndex < requiredFields.length && completedFields.has(requiredFields[nextIndex].id)) {
      nextIndex++;
    }

    if (nextIndex < requiredFields.length) {
      navigateToField(nextIndex);
    } else if (allFieldsComplete) {
      setSigningState('ready_to_complete');
    }
  };

  const navigateToField = (index: number) => {
    if (!requiredFields[index]) return;
    setCurrentFieldIndex(index);

    const field = requiredFields[index];

    // Skip date fields automatically (already auto-filled)
    if (field.type === 'date') {
      // Date fields are already completed, move to next
      setTimeout(() => {
        const nextIndex = index + 1;
        if (nextIndex < requiredFields.length) {
          navigateToField(nextIndex);
        } else {
          setSigningState('ready_to_complete');
        }
      }, 300); // Short delay to show scroll
      return;
    }

    // Highlight the field and scroll into view
    setActiveFieldId(field.id);

    // Scroll field into view after brief delay to ensure field is rendered
    setTimeout(() => {
      const fieldElement = document.querySelector(`[data-field-id="${field.id}"]`);
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    // For signature/initials fields with saved data, auto-apply
    if (field.type === 'signature' || field.type === 'initials') {
      const savedData = field.type === 'initials' ? savedInitialsData : savedSignatureData;

      if (savedData && !completedFields.has(field.id)) {
        // Auto-apply saved signature after brief delay to show field
        setTimeout(() => {
          applySignature(field.id, field.type as 'signature' | 'initials', savedData.data, savedData.method, {
            text: savedData.text,
            font: savedData.font,
          });

          // Auto-advance to next field
          const nextIndex = index + 1;
          if (nextIndex < requiredFields.length) {
            navigateToField(nextIndex);
          } else {
            setSigningState('ready_to_complete');
          }
        }, 500); // Delay to show field before auto-applying
      }
      // If no saved signature yet, user must click to open modal
    }
    // For text fields, just highlight - user must type and click Next
  };

  const handleFieldClick = (fieldData: { id: string } | string) => {
    // PDFJSViewer passes the full field object, extract the ID
    const fieldId = typeof fieldData === 'string' ? fieldData : fieldData.id;
    const field = session.fields.find((f) => f.id === fieldId);
    if (!field) return;

    // Auto-start signing if needed
    if (signingState === 'not_started') {
      setSigningState('signing');
    }

    // Handle signature/initial fields
    if (field.type === 'signature' || field.type === 'initials') {
      // Always open modal when clicking - allows user to change signature
      setActiveFieldId(fieldId);
      setModalFieldType(field.type as 'signature' | 'initials');
      setShowSignatureModal(true);
    }
    // Handle text fields
    else if (field.type === 'text') {
      // Open text input modal
      setActiveFieldId(fieldId);
      setShowTextInputModal(true);
    }
  };

  const applySignature = (
    fieldId: string,
    fieldType: 'signature' | 'initials',
    imageData: string,
    method: 'draw' | 'type' | 'upload',
    metadata?: { text?: string; font?: string }
  ) => {
    // Mark field as completed
    setCompletedFields((prev) => new Set(prev).add(fieldId));

    // Store signature
    setFieldSignatures((prev) => {
      const newMap = new Map(prev);
      newMap.set(fieldId, { data: imageData, method, ...metadata });
      return newMap;
    });

    // Save for reuse
    if (fieldType === 'signature') {
      setSavedSignatureData({ data: imageData, method, ...metadata });
      setSignatureData(imageData);
    } else {
      setSavedInitialsData({ data: imageData, method, ...metadata });
    }
  };

  const handleSignatureComplete = async (
    signatureImageData: string,
    method: 'draw' | 'type' | 'upload',
    metadata?: { text?: string; font?: string }
  ) => {
    if (!activeFieldId) return;

    const field = session.fields.find((f) => f.id === activeFieldId);
    if (!field) return;

    // Apply signature to current field
    applySignature(activeFieldId, field.type as 'signature' | 'initials', signatureImageData, method, metadata);

    // Close modal
    setShowSignatureModal(false);
    setActiveFieldId(null);

    // Auto-apply to all other same-type fields (DocuSign-style)
    const sameTypeFields = session.fields.filter(
      (f) => f.type === field.type && f.id !== activeFieldId && !completedFields.has(f.id)
    );

    sameTypeFields.forEach((f) => {
      applySignature(f.id, f.type as 'signature' | 'initials', signatureImageData, method, metadata);
    });

    // Auto-advance to next field
    const fieldIndex = requiredFields.findIndex((f) => f.id === activeFieldId);
    if (fieldIndex !== -1) {
      const nextIndex = fieldIndex + 1;
      if (nextIndex < requiredFields.length) {
        setTimeout(() => navigateToField(nextIndex), 100);
      } else {
        setSigningState('ready_to_complete');
      }
    }
  };

  const handleTextFieldChange = (fieldId: string, value: string) => {
    const field = session.fields.find((f) => f.id === fieldId);
    if (!field) return;

    const hasContent = value.trim().length > 0;

    // Update completion status
    setCompletedFields((prev) => {
      const newSet = new Set(prev);
      hasContent ? newSet.add(fieldId) : newSet.delete(fieldId);
      return newSet;
    });

    // Update field values
    if (hasContent) {
      setFieldValues((prev) => ({
        ...prev,
        [fieldId]: {
          value,
          type: 'text',
          coordinates: {
            x: field.rect.x1,
            y: field.rect.y1,
            width: field.rect.x2 - field.rect.x1,
            height: field.rect.y2 - field.rect.y1,
            page: field.page,
          },
          signerId: session.signer.id.toString(),
          signerEmail: session.signer.email,
        },
      }));
    } else {
      setFieldValues((prev) => {
        const newValues = { ...prev };
        delete newValues[fieldId];
        return newValues;
      });
    }
  };

  const handleTextInputComplete = (text: string) => {
    if (!activeFieldId) return;

    const currentActiveFieldId = activeFieldId;

    // Update the field value
    handleTextFieldChange(activeFieldId, text);

    // Close modal
    setShowTextInputModal(false);
    setActiveFieldId(null);

    // Auto-advance to next field - use setTimeout to allow state to update
    setTimeout(() => {
      const fieldIndex = requiredFields.findIndex((f) => f.id === currentActiveFieldId);
      if (fieldIndex !== -1) {
        const nextIndex = fieldIndex + 1;

        // Check if all fields will be complete after this update
        const newCompletedCount = completedFields.size + (text.trim().length > 0 ? 1 : 0);
        const allComplete = newCompletedCount >= requiredFields.length;

        if (nextIndex < requiredFields.length) {
          // Navigate to next incomplete field
          navigateToField(nextIndex);
        } else if (allComplete) {
          // We're at the last field AND all fields are complete
          setSigningState('ready_to_complete');
        }
        // Otherwise stay in 'signing' state with Next button visible
      }
    }, 100);
  };

  const handleComplete = async () => {
    if (!allFieldsComplete) return;
    onComplete(signatureData, savedInitialsData?.data || '', fieldValues);
  };

  // Convert fields to format expected by PDFJSViewer
  const pdfJsFields = session.fields.map((field) => {
    // Format field type as label (signature → Signature, initials → Initials, date → Date)
    const typeLabel = field.type === 'date'
      ? 'Date'
      : field.type.charAt(0).toUpperCase() + field.type.slice(1);

    return {
      id: field.id,
      type: field.type,
      page: field.page,
      x: field.rect.x1,
      y: field.rect.y1,
      w: field.rect.x2 - field.rect.x1,
      h: field.rect.y2 - field.rect.y1,
      signer: session.signer.id.toString(),
      req: field.required,
      data: {
        label: typeLabel, // Just the field type, no email
        placeholder: field.placeholder,
        signatureImage: fieldSignatures.get(field.id)?.data,
        value: fieldValues[field.id]?.value,
        completed: completedFields.has(field.id),
      },
    };
  });

  return (
    <div className={styles.signingContainer}>
      <div className={styles.header}>
        <div className={styles.headerBottom}>
          <div className={styles.headerLeft}>
            {/* Desktop: document info stacked */}
            <div className={styles.documentInfo}>
              <span className={styles.documentNameDesktop}>{session.document.title || 'Document'}</span>
              <span className={styles.poweredBy}>Powered by Propdocs</span>
            </div>

            {/* Mobile: document info stacked (same structure, different styles) */}
            <div className={styles.mobileDocumentInfo}>
              <span className={styles.documentName}>{session.document.title || 'Document'}</span>
              <span className={styles.poweredByMobile}>Powered by Propdocs</span>
            </div>
          </div>

          <div className={styles.headerCenter}></div>

          <div className={styles.headerRight}>
            {signingState === 'signing' && (
              <span className={styles.fieldCounter}>
                {remainingFields} field{remainingFields !== 1 ? 's' : ''} left
              </span>
            )}
            {signingState === 'not_started' && (
              <Button variant="primary" size="lg" onClick={handleStart} className={styles.actionButton}>
                Start Signing
              </Button>
            )}
            {signingState === 'signing' && !allFieldsComplete && (
              <Button variant="primary" size="lg" onClick={handleNext} className={styles.actionButton}>
                Next
              </Button>
            )}
            {(signingState === 'signing' || signingState === 'ready_to_complete') && allFieldsComplete && (
              <Button variant="success" size="lg" onClick={handleComplete} className={styles.actionButton}>
                Finish Signing
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.documentContainer}>
        <PDFJSViewer
          pdfUrl={session.document.pdf_url}
          mode="read-only"
          showThumbnails={false}
          savedPdfFormFields={[]} // Don't show form fields - PDF is flattened
          savedPdfAnnotations={[]} // Don't show annotations - PDF is flattened
          hideComments={true}
          hideFormFieldsAndAnnotations={true} // Hide overlays - PDF is flattened for signing
          fieldOverlayProps={{
            fields: pdfJsFields, // Only show signature/initials fields for signing
            selectedFieldId: activeFieldId,
            onSelectField: (id) => id && handleFieldClick(id),
            onUpdateField: () => {}, // Not used in signing mode
            onDeleteField: () => {}, // Not used in signing mode
            readOnlyMode: true, // Enable signing mode styling
          }}
        />
      </div>

      {showSignatureModal && (
        <SignatureModal
          fieldType={modalFieldType}
          existingSignature={modalFieldType === 'initials' ? savedInitialsData?.data : savedSignatureData?.data}
          existingMethod={modalFieldType === 'initials' ? savedInitialsData?.method : savedSignatureData?.method}
          existingText={modalFieldType === 'initials' ? savedInitialsData?.text : savedSignatureData?.text}
          existingFont={modalFieldType === 'initials' ? savedInitialsData?.font : savedSignatureData?.font}
          onComplete={handleSignatureComplete}
          onClose={() => {
            setShowSignatureModal(false);
            setActiveFieldId(null);
          }}
        />
      )}

      <TextInputModal
        isOpen={showTextInputModal}
        fieldType="text"
        initialValue={activeFieldId ? (fieldValues[activeFieldId]?.value || '') : ''}
        onComplete={handleTextInputComplete}
        onClose={() => {
          setShowTextInputModal(false);
          setActiveFieldId(null);
        }}
      />
    </div>
  );
};
